import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "src/api";
import {
  OpsanioHardwareInventory,
  OpsanioHardwareSummary,
  useAnketaAuth,
} from "src/auth";

const EMPTY_COUNTS = { total: 0, inUse: 0, free: 0, failed: 0 };

export const EMPTY_HARDWARE_SUMMARY: OpsanioHardwareSummary = {
  server: EMPTY_COUNTS,
  switch: EMPTY_COUNTS,
  dac: EMPTY_COUNTS,
  total: EMPTY_COUNTS,
};

export interface UseHardwareInventoryResult {
  inventory: OpsanioHardwareInventory | null;
  summary: OpsanioHardwareSummary;
  loading: boolean;
  /** Whether a fetch has completed at least once. */
  loaded: boolean;
  /** Transport/authorization failure - counts are unknown, not zero. */
  error: string | null;
  reload: () => void;
}

/**
 * Fleet hardware inventory from NetBox (servers, switches, DAC cables) with the
 * in-use/free split.
 *
 * One request for the whole pool; `inventory.failed` names the kinds NetBox
 * could not answer for, so a failed lookup is never rendered as an empty spare
 * pool. Fetches once on mount and on demand via `reload`.
 */
export const useHardwareInventory = (): UseHardwareInventoryResult => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();

  const [inventory, setInventory] = useState<OpsanioHardwareInventory | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const runningRef = useRef(false);

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string",
    [keycloak],
  );

  const reload = useCallback(async () => {
    if (!canFetch || runningRef.current) return;
    runningRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const { success, data, error: apiError } = await Get<any>({
        path: "v2/netbox/inventory",
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || apiError) {
        setError(
          apiError?.message ?? "NetBox hardware inventory could not be loaded.",
        );
        setInventory(null);
      } else {
        setInventory(
          ((data as any)?.data as OpsanioHardwareInventory) ?? {
            items: [],
            summary: EMPTY_HARDWARE_SUMMARY,
            failed: [],
          },
        );
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "NetBox hardware inventory could not be loaded.",
      );
      setInventory(null);
    } finally {
      setLoading(false);
      setLoaded(true);
      runningRef.current = false;
    }
  }, [Get, canFetch, keycloak]);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch]);

  return {
    inventory,
    summary: inventory?.summary ?? EMPTY_HARDWARE_SUMMARY,
    loading,
    loaded,
    error,
    reload,
  };
};
